import { ButtonBase, Tooltip, useTheme } from '@mui/material';
import { QueryVolumeLabel } from './QueryVolumeLabel';
import { Box, Stack, Typography } from './mui-system';
import {
  HEATMAP_LEGEND,
  heatmapPalette,
  heatmapLabel,
  heatmapPattern,
  heatmapSlots,
  type HeatmapTone,
} from '../../../api/src/fakes/serp-heatmap';
import type { FakesMatrixRow, FakesResult } from '../api/types';
export function SerpHeatmap({
  rows,
  top,
  total,
  page,
  groupName,
  disabled,
  onSelect,
}: {
  rows: FakesMatrixRow[];
  top: number;
  total: number;
  page: number;
  groupName: string;
  disabled: boolean;
  onSelect: (result: FakesResult, row: FakesMatrixRow) => void;
}) {
  const theme = useTheme(),
    p = heatmapPalette(theme.palette.mode, theme.serpColors);
  const paint = (tone: HeatmapTone, noise = false) => ({
    backgroundColor: p[tone],
    backgroundImage: heatmapPattern(tone, noise)
      ? 'repeating-linear-gradient(135deg,transparent 0 3px,#8886 3px 4px)'
      : undefined,
  });
  const grid = {
    display: 'grid',
    gridTemplateColumns: {
      xs: `repeat(${Math.min(10, top)},minmax(0,1fr))`,
      md: `repeat(${top},minmax(0,1fr))`,
    },
    gap: '3px',
    alignItems: 'center',
  };
  return (
    <Box
      component="section"
      aria-label="Тепловая карта выдачи"
      sx={{
        m: { xs: 1, sm: 2 },
        p: { xs: 1, sm: 2 },
        borderRadius: 2,
        border: `1px solid ${p.border}`,
        bgcolor: p.background,
        color: p.text,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <Typography fontWeight={650}>Карта позиций · {groupName}</Typography>
      <Typography fontSize={12} sx={{ color: p.muted, mb: 2 }}>
        Страница {page} · показано {rows.length} из {total} запросов. На телефоне — первые{' '}
        {Math.min(10, top)} позиций. Нажмите кирпичик для разбора.
      </Typography>
      <Box sx={{ maxHeight: 480, overflowY: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: 'minmax(110px,1fr) minmax(0,2fr)' },
            gap: 1,
          }}
        >
          <Box sx={{ display: { xs: 'none', sm: 'block' } }} />
          <Box sx={grid}>
            {Array.from({ length: top }, (_, i) => (
              <Typography
                key={i}
                fontSize={10}
                textAlign="center"
                sx={{ color: p.muted, display: i >= 10 ? { xs: 'none', md: 'block' } : 'block' }}
              >
                {i + 1}
              </Typography>
            ))}
          </Box>
          {rows.map((row) => (
            <Box key={row.queryId} sx={{ display: 'contents' }}>
              <Box sx={{ minWidth: 0, py: 1, alignSelf: 'center' }}>
                <Typography fontSize={12} sx={{ overflowWrap: 'anywhere' }}>
                  {row.priority ? '★ ' : ''}
                  {row.query}
                </Typography>
                <QueryVolumeLabel volume={row.volume} />
                {row.status !== 'complete' && (
                  <Typography fontSize={10} sx={{ color: p.muted }}>
                    Нет полных данных
                  </Typography>
                )}
              </Box>
              <Box sx={grid}>
                {heatmapSlots(row.status, row.results, top).map((slot) => (
                  <Box
                    key={slot.rank}
                    sx={{
                      display: slot.rank > 10 ? { xs: 'none', md: 'flex' } : 'flex',
                      gap: '1px',
                      minWidth: 0,
                    }}
                  >
                    {slot.parts.map((part, index) => {
                      const label = `${row.query} · #${slot.rank}: ${heatmapLabel(part.tone, part.result)}`;
                      return (
                        <Tooltip key={index} title={label}>
                          <ButtonBase
                            disabled={disabled || !part.result}
                            aria-label={label}
                            onClick={() => part.result && onSelect(part.result, row)}
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              height: 44,
                              borderRadius: 1,
                              '&:focus-visible': {
                                outline: `2px solid ${p.text}`,
                                outlineOffset: 1,
                              },
                            }}
                          >
                            <Box
                              sx={{
                                ...paint(part.tone, part.result?.relevance?.noise),
                                height: 15,
                                width: '100%',
                                borderRadius: '3px',
                                border: `1px solid ${p.border}`,
                              }}
                            />
                          </ButtonBase>
                        </Tooltip>
                      );
                    })}
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
      {!rows.length && <Typography fontSize={12}>Нет запросов в выбранном срезе.</Typography>}
      <Stack direction="row" flexWrap="wrap" gap={1.5} mt={2}>
        {HEATMAP_LEGEND.map(([tone, label]) => (
          <Stack key={tone} direction="row" alignItems="center" gap={0.5}>
            <Box
              sx={{
                ...paint(tone as HeatmapTone),
                width: 13,
                height: 10,
                borderRadius: '2px',
                border: `1px solid ${p.border}`,
              }}
            />
            <Typography fontSize={10} sx={{ color: p.muted }}>
              {label}
            </Typography>
          </Stack>
        ))}
      </Stack>
      <Typography fontSize={10} sx={{ color: p.muted, mt: 1 }}>
        Штриховка также обозначает шум; несколько ресурсов на позиции делят кирпичик.
      </Typography>
    </Box>
  );
}
